import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhySection from '../components/WhySection';
import HowWeHelpSection from '../components/HowWeHelpSection';
import { Award, Users, Target, TrendingUp, CheckCircle, Star } from 'lucide-react';

const WhyChooseUsPage = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6"><span className="bg-indigo-100 text-indigo-700 px-6 py-2 rounded-full text-sm font-semibold">WHY CHOOSE US</span></div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">Why CareerLift?</h1>
          <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed">We don't just teach—we transform. Here's what makes us different from every other training platform.</p>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-12">CareerLift vs Others</h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl shadow-xl overflow-hidden">
              <thead className="bg-indigo-700 text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Feature</th>
                  <th className="px-6 py-4 text-center">CareerLift ✅</th>
                  <th className="px-6 py-4 text-center">Other Platforms ❌</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-indigo-50 transition-colors">
                  <td className="px-6 py-4 font-semibold">Structured Learning Path</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">4-Step Framework</td>
                  <td className="px-6 py-4 text-center text-slate-500">Random Courses</td>
                </tr>
                <tr className="hover:bg-indigo-50 transition-colors">
                  <td className="px-6 py-4 font-semibold">Live Mentor Support</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">Weekly Sessions</td>
                  <td className="px-6 py-4 text-center text-slate-500">Pre-recorded Only</td>
                </tr>
                <tr className="hover:bg-indigo-50 transition-colors">
                  <td className="px-6 py-4 font-semibold">Hands-On Projects</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">Every Course</td>
                  <td className="px-6 py-4 text-center text-slate-500">Limited/None</td>
                </tr>
                <tr className="hover:bg-indigo-50 transition-colors">
                  <td className="px-6 py-4 font-semibold">Career Support</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">Resume + Interview Prep</td>
                  <td className="px-6 py-4 text-center text-slate-500">Not Included</td>
                </tr>
                <tr className="hover:bg-indigo-50 transition-colors">
                  <td className="px-6 py-4 font-semibold">Job Market Focus</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">Australian Market</td>
                  <td className="px-6 py-4 text-center text-slate-500">Generic/Global</td>
                </tr>
                <tr className="hover:bg-indigo-50 transition-colors">
                  <td className="px-6 py-4 font-semibold">Community Support</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">Active Cohorts</td>
                  <td className="px-6 py-4 text-center text-slate-500">Isolated Learning</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <WhySection />
      <HowWeHelpSection />
      
      {/* Our Unique Advantages */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-16">What Makes Us Truly Different</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-indigo-600">
              <Award className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Australian Job Market Expertise</h3>
              <p className="text-slate-700 leading-relaxed mb-4">We're not a generic international platform. We understand the Australian IT job market inside-out: which companies are hiring, what skills they want, and how to get past their interview processes.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Salary negotiation for Australian market</span></li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Local company connections and referrals</span></li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Understanding visa requirements for international students</span></li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-indigo-600">
              <Users className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Small Cohort Model</h3>
              <p className="text-slate-700 leading-relaxed mb-4">We deliberately keep class sizes small (max 50 per cohort) so every student gets personalized attention. You're not just a number—you're part of a tight-knit community.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Personal mentor relationships</span></li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Peer collaboration on projects</span></li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Lifetime alumni network access</span></li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-green-600">
              <Target className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Results-Driven Curriculum</h3>
              <p className="text-slate-700 leading-relaxed mb-4">Every lesson, project, and module is designed with one goal: get you employed. We don't teach theory for theory's sake. Every skill you learn is immediately applicable to real jobs.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Employer-validated curriculum</span></li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Projects that mirror real company work</span></li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Updated quarterly with market trends</span></li>
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-t-4 border-amber-600">
              <TrendingUp className="w-12 h-12 text-amber-600 mb-4" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Accountability & Momentum</h3>
              <p className="text-slate-700 leading-relaxed mb-4">We don't let you fall behind. Regular check-ins, progress tracking, and mentor accountability ensure you keep moving forward—even when motivation dips.</p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Weekly progress reviews with mentors</span></li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Milestone celebrations and achievements</span></li>
                <li className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" /><span className="text-slate-700">Personal dashboard to track every step</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-slate-900 mb-16">What Students Say</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Michael Chen", role: "University Student → AI Engineer", quote: "CareerLift was the missing piece. My university taught theory, but CareerLift taught me how to actually build things. Got a job offer before even graduating!", rating: 5 },
              { name: "Priya Sharma", role: "Career Changer → Cloud Architect", quote: "I was terrified of changing careers at 30. The mentors at CareerLift believed in me even when I didn't believe in myself. Now I'm earning double what I made before.", rating: 5 },
              { name: "David Wilson", role: "Recent Grad → DevOps Engineer", quote: "Best decision I ever made. The structured approach, the projects, the interview prep—everything was on point. Landed my dream job within 4 months.", rating: 5 }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-6 shadow-lg">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-amber-400 fill-current" viewBox="0 0 20 20"><path d="M10 15l-5.878 3.09 1.123-6.545L.49 6.91l6.562-.954L10 0l2.948 5.956 6.562.954-4.755 4.635 1.123 6.545z" /></svg>
                  ))}
                </div>
                <p className="text-slate-700 italic mb-4">"{testimonial.quote}"</p>
                <div className="border-t pt-4">
                  <p className="font-bold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-600">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="py-20 bg-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Experience the CareerLift Difference</h2>
          <p className="text-xl text-indigo-100 mb-8">Join the students who chose transformation over information</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => window.location.href = '/contact'} className="bg-white text-indigo-700 font-semibold px-10 py-4 rounded-md hover:bg-gray-50 transition-colors duration-300">Start Your Journey</button>
            <button onClick={() => window.location.href = '/programs'} className="bg-transparent text-white font-semibold px-10 py-4 rounded-md border-2 border-white hover:bg-white/10 transition-colors duration-300">View Programs</button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default WhyChooseUsPage;
